/**
 * Composable gesture hooks for the viewport.
 *
 * `useViewport` used to be one ~700-line hook that owned animation plus
 * wheel, drag, and keyboard listeners inline. That made the interaction
 * logic untestable in isolation — only the pure `viewport.ts` slice had
 * coverage. These hooks split each input source into its own effect over
 * the shared `ViewportStore`, so each can be mounted and exercised on its
 * own.
 *
 * Every input hook follows the same contract: read the current viewport
 * synchronously via `store.get()`, compute the next value (including any
 * DOM measurement through the injected `measure`), and commit once with
 * `store.set()`. No side effects run inside a store updater.
 */
import { useCallback, useRef } from "react";
import { useEffect } from "react";
import type { SpaceLike } from "@archlang/scene/board";
import {
  type MeasureBodyRect,
  type Viewport,
  clampPanRoam,
  clampZoom,
  lerpViewport,
  maybeRebase,
  planFlight,
  pan as panVp,
  zoomAt,
} from "@archlang/scene/board";
import { diveBodyRect } from "./bodyRect";
import { observeResize } from "./sharedResizeObserver";
import { type WheelIntent, classifyWheel } from "./wheelNav";
import type { ViewportStore } from "./viewportStore";

/** Body-rect adapter injected into the pure `maybeRebase` — the same solver
 *  slot rect the nested preview renders at (see `bodyRect.ts`). Stable
 *  (module-level) so it never needs to appear in a dependency array. */
const measure: MeasureBodyRect = (item) => diveBodyRect(item);

/**
 * Zoom-input gains — how much each input device moves zoom per unit of
 * raw delta. Centralised so the feel is tuned in one place rather than as
 * magic multipliers buried in the handlers.
 *  - `pinch`: trackpad pinch on Chromium (ctrl-wheel, small deltaY/frame).
 *  - `safariPinch`: multiplier on `log(gestureRatio)` from Safari's
 *    `gesturechange` — zoom moves as gestureRatio^gain. Chromium reports
 *    the same physical pinch as ctrl-wheel with deltaY ≈ 100·ln(ratio),
 *    so its effective exponent is 100 × `pinch` = 3. Match it exactly:
 *    cross-browser parity beats physical 1:1 (which felt ~3× slower than
 *    Chrome), and the old 5 overshot into twitchy. If `pinch` is retuned,
 *    keep this at 100 × pinch.
 *  - `keyboard`: ⌘+ / ⌘- step.
 */
const ZOOM_GAIN = {
  pinch: 0.03,
  safariPinch: 3,
  keyboard: 0.2,
} as const;

/**
 * Touch-gesture tuning. Pinch needs no gain — zoom follows the finger
 * distance ratio exactly (spread 2× → zoom 2×), which is the only mapping
 * that keeps the content pinned under both fingers.
 */
const TOUCH = {
  /** Finger travel (px) before a single touch becomes a pan — the tap slop. */
  panSlop: 8,
  /** Max press duration (ms) for a touch to still count as a tap. */
  tapMaxMs: 350,
  /** Max gap (ms) and travel (px) between two taps to form a double-tap. */
  doubleTapMs: 300,
  doubleTapSlop: 40,
  /** Log-space zoom step for a background double-tap (ln 2 = one doubling). */
  doubleTapZoom: Math.log(2),
  /** Release speed (px/ms) below which a pan just stops (no inertia). */
  flickMinSpeed: 0.25,
  /** Velocity cap (px/ms) — jsdom/synthetic events can report near-zero dt. */
  flickMaxSpeed: 4,
  /** Inertia decays as exp(-t/tau); 325ms matches the iOS scroll feel. */
  inertiaTau: 325,
  /** Speed (px/ms) at which the inertia glide is considered settled. */
  inertiaStopSpeed: 0.02,
} as const;

/** Fired by a focal item when its touch long-press wants to start a node
 *  drag. Cancelable: the board's touch-gesture layer calls preventDefault
 *  when a pan/pinch already owns the pointer; otherwise it releases the
 *  pointer to the node. */
export const NODE_DRAG_CLAIM_EVENT = "archlang-nodedrag";

const ANIM_DURATION = 260;
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
// Time profile for the van Wijk flight: the path itself is perceptually
// uniform, so the profile should stay CLOSE to constant velocity — an
// ease-in-out (sine/cubic) concentrates speed mid-flight (sine peaks at ~1.57×
// the average), and that fast middle strobes on a sample-and-hold display,
// reading as mid-flight lag. Trapezoidal velocity instead: smooth quadratic
// ramps over the first/last RAMP of the time, flat cruise between — peak speed
// only 1/(1 - RAMP) = 1.25× the average.
const RAMP = 0.2;
const easeTrapezoid = (t: number): number => {
  const vPeak = 1 / (1 - RAMP);
  if (t < RAMP) return (vPeak * t * t) / (2 * RAMP);
  if (t > 1 - RAMP) return 1 - (vPeak * (1 - t) * (1 - t)) / (2 * RAMP);
  return vPeak * (t - RAMP / 2);
};

/** Shared dependencies every gesture hook needs. */
export interface GestureDeps {
  readonly store: ViewportStore;
  readonly containerRef: React.RefObject<HTMLElement | null>;
  readonly rootRef: React.MutableRefObject<SpaceLike>;
  readonly userNavigatedRef: React.MutableRefObject<boolean>;
  readonly cancelAnim: () => void;
  /** Fired when the user drives the board directly (wheel / drag / keys). Lets
   *  the camera operator back off for a moment so its auto-motion doesn't fight
   *  a hand on the board. Distinct from `userNavigatedRef` (which is also set by
   *  programmatic commands like flyTo) — this fires ONLY on real manual input. */
  readonly onUserGesture?: () => void;
}

export interface ViewportAnim {
  /** Animate from the live viewport to `target` over ANIM_DURATION ms. */
  readonly animateTo: (target: Viewport, onDone?: () => void) => void;
  /** Defer an `animateTo` to the next frame — used by pop-out so the
   *  instant focal change paints before the outward animation starts. */
  readonly scheduleAnimateTo: (target: Viewport) => void;
  /** Cancel any in-flight animation. Call before committing a user gesture. */
  readonly cancelAnim: () => void;
}

/** Owns the animation rAF loop. Returns the animation controls plus a
 *  `cancelAnim` the other gesture hooks use to interrupt it.
 *
 *  Pass `screenSize` to fly along the van Wijk smooth pan-zoom path with a
 *  distance-proportional duration (comfortable for deep dives + long pans);
 *  without it, falls back to the legacy fixed-duration lerp. */
export function useViewportAnim(
  store: ViewportStore,
  screenSize?: () => { width: number; height: number },
): ViewportAnim {
  const animRef = useRef<number | null>(null);
  const cancelAnim = useCallback(() => {
    if (animRef.current !== null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }, []);

  const animateTo = useCallback(
    (target: Viewport, onDone?: () => void) => {
      cancelAnim();
      const from = store.get();
      // Plan the smooth flight when we can measure the screen (its size anchors
      // the world-centre math); zero size (unmounted) falls back to the lerp.
      const screen = screenSize?.();
      const flight =
        screen && screen.width > 0 && screen.height > 0
          ? planFlight(from, target, screen)
          : null;
      const duration = flight?.duration ?? ANIM_DURATION;
      const sample = flight
        ? (t: number) => flight.at(easeTrapezoid(t))
        : (t: number) => lerpViewport(from, target, easeInOutCubic(t));
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        store.set(sample(t));
        if (t < 1) {
          animRef.current = requestAnimationFrame(step);
        } else {
          animRef.current = null;
          onDone?.();
        }
      };
      animRef.current = requestAnimationFrame(step);
    },
    [cancelAnim, store, screenSize],
  );

  const scheduleAnimateTo = useCallback(
    (target: Viewport) => {
      animRef.current = requestAnimationFrame(() => animateTo(target));
    },
    [animateTo],
  );

  return { animateTo, scheduleAnimateTo, cancelAnim };
}

/** Wheel / trackpad / Safari-gesture / IntelliJ-bridge zoom + pan.
 *
 *  `touchCountRef` (live count of touch pointers on the board, written by
 *  `useTouchGestures`) disambiguates Safari's gesture events: on iOS a touch
 *  pinch fires BOTH pointer events (handled by the touch hook) and gesture
 *  events — applying the gesture scale too would double the zoom. A macOS
 *  trackpad pinch fires gesture events with zero touch pointers, so it still
 *  zooms here. */

/**
 * Cached container-rect reader shared by every gesture effect (wheel / drag /
 * touch). The container's SCREEN rect (position + size) only changes on resize
 * or an ancestor scroll — never during a pan/zoom gesture (only the content
 * transforms). Reading `getBoundingClientRect()` per gesture event forces a
 * synchronous layout on EVERY frame (the per-frame reflow the FullArchitecture
 * pan trace showed: Layout + RecalcStyle 1×/frame). Cache it; invalidate only
 * when it can actually move. `dispose()` unwires the listeners in the effect
 * cleanup.
 */
function createRectCache(el: HTMLElement): { getRect: () => DOMRect; dispose: () => void } {
  let cachedRect: DOMRect | null = null;
  const getRect = (): DOMRect => (cachedRect ??= el.getBoundingClientRect());
  const invalidate = (): void => {
    cachedRect = null;
  };
  window.addEventListener("resize", invalidate, { passive: true });
  // Capture-phase: any scroll anywhere in the ancestor chain can shift the
  // container. A gesture itself fires no scroll, so this never runs mid-pan.
  window.addEventListener("scroll", invalidate, { capture: true, passive: true });
  // Shared observer, NOT a per-effect `new ResizeObserver` — every gesture
  // effect creates a rect cache, so direct construction multiplied native
  // observers per board (the exact allocation sharedResizeObserver exists to
  // collapse) and an out-of-band construction breaks the observer seam the
  // viewport tests drive resizes through.
  const disposeResize = observeResize(el, invalidate);
  return {
    getRect,
    dispose: () => {
      window.removeEventListener("resize", invalidate);
      window.removeEventListener("scroll", invalidate, { capture: true });
      disposeResize();
    },
  };
}

export function useWheelZoom(
  deps: GestureDeps,
  wheel: boolean | undefined,
  touchCountRef?: React.MutableRefObject<number>,
  enabled = true,
): void {
  const { store, containerRef, rootRef, userNavigatedRef, cancelAnim, onUserGesture } = deps;
  useEffect(() => {
    if (!enabled) return; // embed posture: no wheel / Safari-gesture listeners
    const el = containerRef.current;
    if (!el) return;
    // High-rate input devices (Mac trackpad, Magic Mouse, JCEF heavyweight
    // browsers) can emit wheel events well above 60 Hz. Driving a setState
    // per event triggers an SVG re-render per event, which on Retina
    // saturates the compositor and makes the gesture feel laggy and
    // unpredictable. We accumulate deltas into a single per-frame flush
    // so at most one viewport update lands per rAF tick.
    let pendingZoomDelta = 0;
    let pendingPanDx = 0;
    let pendingPanDy = 0;
    let pendingPivot = { x: 0, y: 0 };
    let pendingRect: DOMRect | null = null;
    let wheelRaf = 0;
    const { getRect, dispose: disposeRectCache } = createRectCache(el);
    const flushWheel = () => {
      wheelRaf = 0;
      const rect = pendingRect;
      if (!rect) return;
      const sx = pendingPivot.x;
      const sy = pendingPivot.y;
      const zoomDelta = pendingZoomDelta;
      const panDx = pendingPanDx;
      const panDy = pendingPanDy;
      pendingZoomDelta = 0;
      pendingPanDx = 0;
      pendingPanDy = 0;
      pendingRect = null;
      if (zoomDelta === 0 && panDx === 0 && panDy === 0) return;
      const size = { width: rect.width, height: rect.height };
      const r = rootRef.current;
      // Read the current viewport synchronously, apply zoom/pan + rebase
      // (which measures the DOM) entirely outside the store mutation, then
      // commit the final pure value once.
      let next = store.get();
      if (zoomDelta !== 0) {
        next = zoomAt(next, sx, sy, zoomDelta, size.width, size.height);
        next = maybeRebase(r, next, size, { x: sx, y: sy }, measure);
        next = clampZoom(r, next, sx, sy, size.width, size.height);
      }
      if (panDx !== 0 || panDy !== 0) {
        next = panVp(next, panDx, panDy);
        next = maybeRebase(r, next, size, undefined, measure);
      }
      // Space border: never let the focal space drift more than a slack
      // screen past any edge (rebase first — the clamp is per-focal).
      next = clampPanRoam(r, next, size);
      store.set(next);
    };
    // Shared preamble for every accumulated input: mark the gesture as user
    // navigation, interrupt any auto-motion, stash the pivot, and make sure
    // a flush is scheduled. Both the wheel path and the Safari gesture path
    // funnel through here so they get identical per-frame treatment.
    const beginInput = (sx: number, sy: number, rect: DOMRect) => {
      userNavigatedRef.current = true;
      onUserGesture?.();
      cancelAnim();
      pendingRect = rect;
      pendingPivot = { x: sx, y: sy };
      if (!wheelRaf) wheelRaf = requestAnimationFrame(flushWheel);
    };
    // Accumulate a log-space zoom delta pivoting at (sx, sy). `flushWheel`
    // turns it into zoomAt + maybeRebase (auto dive-in/pop-out) + clampZoom
    // once per rAF tick.
    const applyZoomDelta = (delta: number, sx: number, sy: number, rect: DOMRect) => {
      beginInput(sx, sy, rect);
      pendingZoomDelta += delta;
    };
    // Routes an already-classified wheel intent into the per-frame accumulator
    // pivoting at (sx, sy). Both the DOM listener and the IntelliJ native
    // bridge classify a `WheelSample` via the pure `classifyWheel`, then hand
    // the intent here.
    const applyIntent = (
      intent: WheelIntent,
      sx: number,
      sy: number,
      rect: DOMRect,
    ) => {
      if (intent.kind === "zoom") {
        applyZoomDelta(intent.zoomDelta, sx, sy, rect);
      } else {
        beginInput(sx, sy, rect);
        pendingPanDx += intent.panDx;
        pendingPanDy += intent.panDy;
      }
    };
    const onWheel = (e: WheelEvent) => {
      const intent = classifyWheel(
        {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          shiftKey: e.shiftKey,
        },
        { pinch: ZOOM_GAIN.pinch },
      );
      // Embeds can disable direct scroll-pan: a wheel that resolves to a pan
      // bubbles to the page (so it scrolls past the canvas), while a zoom is
      // always handled.
      if (wheel === false && intent.kind === "pan") return;
      e.preventDefault();
      const rect = getRect();
      applyIntent(intent, e.clientX - rect.left, e.clientY - rect.top, rect);
    };
    el.addEventListener("wheel", onWheel, { passive: false });

    // Native bridge for the IntelliJ plugin: it intercepts trackpad wheel
    // events on the Swing side, coalesces them on the EDT, and dispatches
    // a single per-frame call here. This avoids JCEF's DOM-level wheel
    // queue which on macOS backs up and causes "drunk" pan/zoom drift.
    type WheelBridge = (
      dx: number,
      dy: number,
      isPinch: boolean,
      isMouseWheel: boolean,
      sx: number,
      sy: number,
    ) => void;
    const bridge: WheelBridge = (dx, dy, isPinch, isMouseWheel, sx, sy) => {
      const rect = getRect();
      // sx/sy from Java are relative to the JCEF component which is the
      // tool window's content pane. The board container fills it, so the
      // coordinates align 1:1. Rebuild a WheelSample from the Java-side flags
      // so the bridge flows through the SAME classifier (and inherits the same
      // device-aware zoom/pan): isPinch → a ctrl-wheel,
      // isMouseWheel → a line-mode detent, otherwise a pixel scroll (pan).
      const intent = classifyWheel(
        {
          deltaX: dx,
          deltaY: dy,
          deltaMode: isMouseWheel ? 1 : 0,
          ctrlKey: isPinch,
          metaKey: false,
          shiftKey: false,
        },
        { pinch: ZOOM_GAIN.pinch },
      );
      applyIntent(intent, sx, sy, rect);
    };
    (window as unknown as { archlangApplyWheel?: WheelBridge }).archlangApplyWheel = bridge;

    // ---- Safari trackpad / touch pinch ----
    // Safari (macOS and iOS) does not deliver pinch as ctrl-wheel the way
    // Chromium and Firefox do — it fires proprietary `gesturestart` /
    // `gesturechange` / `gestureend` events carrying a cumulative `scale`.
    // preventDefault on them is required to suppress Safari's native page
    // zoom — and as a side effect it also stops Safari from emitting
    // ctrl-wheel events for the same gesture. Each gesturechange converts
    // its scale ratio to a log-space delta and feeds the SAME pending/flush
    // pipeline as wheel input, so pinch gets identical per-frame treatment:
    // rAF coalescing, pivot-anchored zoomAt, automatic dive-in/pop-out
    // (maybeRebase) mid-gesture, and edge clamping. Chromium/Firefox never
    // fire these events, so this block is inert there.
    interface GestureEvt extends UIEvent {
      readonly scale: number;
      readonly clientX: number;
      readonly clientY: number;
    }
    let prevScale = 1;
    // Safari latches the gesture's event target at gesturestart (touch-style):
    // every later gesturechange/gestureend dispatches to that SAME node, even
    // after it leaves the DOM. Mid-pinch the board rebases (auto dive-in /
    // pop-out) and unmounts the focal subtree under the fingers — a detached
    // target's events no longer bubble up to the container, so a pinch that
    // crossed a space boundary went dead until the fingers lifted (Chromium's
    // ctrl-wheel stream re-hit-tests every event, so it never had the
    // problem). Fix: on gesturestart, bind the change/end handlers DIRECTLY
    // to the latched target — listeners on the node itself still run when
    // events dispatch to it detached. While the target is still attached the
    // same event reaches us twice (target listener + container bubble);
    // `seenGestureEvents` dedupes.
    const seenGestureEvents = new WeakSet<Event>();
    // Non-null alias: the handlers below are hoisted function declarations,
    // which don't inherit the `if (!el) return` narrowing the way the arrow
    // handlers above do.
    const host: HTMLElement = el;
    let latchedTarget: EventTarget | null = null;
    function unlatchGestureTarget() {
      if (!latchedTarget) return;
      latchedTarget.removeEventListener("gesturechange", onGestureChange as EventListener);
      latchedTarget.removeEventListener("gestureend", onGestureEnd as EventListener);
      latchedTarget = null;
    }
    function onGestureStart(e: Event) {
      e.preventDefault();
      prevScale = (e as GestureEvt).scale || 1;
      cancelAnim();
      unlatchGestureTarget();
      if (e.target && e.target !== host) {
        latchedTarget = e.target;
        latchedTarget.addEventListener("gesturechange", onGestureChange as EventListener, { passive: false });
        latchedTarget.addEventListener("gestureend", onGestureEnd as EventListener, { passive: false });
      }
    }
    function onGestureChange(e: Event) {
      if (seenGestureEvents.has(e)) return;
      seenGestureEvents.add(e);
      e.preventDefault();
      // Touch pinch (iOS) — the pointer-event pinch in useTouchGestures owns
      // the zoom; only suppress Safari's native handling here.
      if (touchCountRef && touchCountRef.current > 0) return;
      const ge = e as GestureEvt;
      const ratio = ge.scale / prevScale;
      if (!Number.isFinite(ratio) || ratio <= 0) return;
      prevScale = ge.scale;
      const rect = getRect();
      applyZoomDelta(
        Math.log(ratio) * ZOOM_GAIN.safariPinch,
        ge.clientX - rect.left,
        ge.clientY - rect.top,
        rect,
      );
    }
    function onGestureEnd(e: Event) {
      if (seenGestureEvents.has(e)) return;
      seenGestureEvents.add(e);
      // Any still-pending delta is flushed by the already-scheduled rAF;
      // the flush rebases + clamps, so no end-of-gesture special case.
      e.preventDefault();
      unlatchGestureTarget();
    }
    el.addEventListener("gesturestart", onGestureStart as EventListener, { passive: false });
    el.addEventListener("gesturechange", onGestureChange as EventListener, { passive: false });
    el.addEventListener("gestureend", onGestureEnd as EventListener, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGestureStart as EventListener);
      el.removeEventListener("gesturechange", onGestureChange as EventListener);
      el.removeEventListener("gestureend", onGestureEnd as EventListener);
      unlatchGestureTarget();
      if (wheelRaf) cancelAnimationFrame(wheelRaf);
      disposeRectCache();
      delete (window as unknown as { archlangApplyWheel?: WheelBridge }).archlangApplyWheel;
    };
  }, [enabled, containerRef, cancelAnim, wheel, store, rootRef, userNavigatedRef, onUserGesture, touchCountRef]);
}

/** Drag-to-pan (threshold-based so clicks still fire). */
export function useDragPan(deps: GestureDeps, enabled = true): void {
  const { store, containerRef, rootRef, userNavigatedRef, cancelAnim, onUserGesture } = deps;
  useEffect(() => {
    if (!enabled) return; // embed posture: no pointer drag-pan listener
    const el = containerRef.current;
    if (!el) return;
    const { getRect, dispose: disposeRectCache } = createRectCache(el);
    let pending: { sx: number; sy: number; pointerId: number } | null = null;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // Touch pointers belong to useTouchGestures (which also pans when the
      // finger lands ON a node — fingers are fat, nodes cover most of a phone
      // screen, and node drag moved to long-press there).
      if (e.pointerType === "touch") return;
      const target = e.target as HTMLElement | null;
      // Focal-level items handle their own drag; HUD & buttons own their clicks.
      if (target?.closest("[data-focal-item], [data-hud], button, [role='button'], a, input, textarea, select")) return;
      pending = { sx: e.clientX, sy: e.clientY, pointerId: e.pointerId };
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pending) return;
      if (!dragging) {
        if (Math.hypot(e.clientX - pending.sx, e.clientY - pending.sy) < 4) return;
        dragging = true;
        el.setPointerCapture(pending.pointerId);
        el.style.cursor = "grabbing";
        lastX = pending.sx;
        lastY = pending.sy;
      }
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      userNavigatedRef.current = true;
      onUserGesture?.();
      cancelAnim();
      const rect = getRect();
      const size = { width: rect.width, height: rect.height };
      const r = rootRef.current;
      const next = maybeRebase(r, panVp(store.get(), dx, dy), size, undefined, measure);
      // Space border — same rule as the wheel/touch pipelines.
      store.set(clampPanRoam(r, next, size));
    };
    const onPointerUp = (e: PointerEvent) => {
      if (dragging) {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
        el.style.cursor = "";
      }
      pending = null;
      dragging = false;
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      disposeRectCache();
    };
  }, [enabled, containerRef, cancelAnim, store, rootRef, userNavigatedRef, onUserGesture]);
}

export interface TouchGestureOptions {
  /** Animated viewport transition — drives the double-tap zoom step. */
  readonly animateTo: (target: Viewport, onDone?: () => void) => void;
  /** Double-tap landed on a board item: open it (dive / zoom-frame).
   *  Return false when the id can't be resolved — the hook then falls back
   *  to the plain zoom step. */
  readonly onItemDoubleTap?: (id: string) => boolean;
  /** Live count of touch pointers on the board — written here, read by
   *  `useWheelZoom` to mute Safari gesture-event zoom during a touch pinch. */
  readonly touchCountRef: React.MutableRefObject<number>;
}

/**
 * Touch input: the phone-grade gesture set, built on pointer events so it
 * works identically on Android Chrome and iOS Safari (Safari's proprietary
 * gesture events are muted while touch pointers are down — see
 * `useWheelZoom`).
 *
 *  - 1 finger: pan, from anywhere — including on top of nodes (node drag is
 *    long-press, owned by the item; arbitrated via NODE_DRAG_CLAIM_EVENT).
 *    Release with speed → inertia glide with exponential decay.
 *  - 2 fingers: pinch zoom anchored at the finger midpoint + midpoint pan,
 *    fed through the same per-frame zoomAt → maybeRebase → clampZoom pipeline
 *    as wheel input, so auto dive-in/pop-out works mid-pinch.
 *  - tap: untouched — the browser's synthesized click drives selection.
 *  - double-tap: open the item under the finger, or zoom in one step on the
 *    void. The synthesized dblclick that may follow is swallowed so the open
 *    doesn't fire twice.
 *
 * Touch pointers are implicitly captured to their pointerdown target, so
 * moves keep arriving (bubbling through the container) even when the finger
 * leaves the element or the board re-renders under it.
 */
export function useTouchGestures(deps: GestureDeps, options: TouchGestureOptions, enabled = true): void {
  const { store, containerRef, rootRef, userNavigatedRef, cancelAnim, onUserGesture } = deps;
  const { animateTo, onItemDoubleTap, touchCountRef } = options;
  useEffect(() => {
    if (!enabled) return; // embed posture: no touch listeners / window blur
    const el = containerRef.current;
    if (!el) return;
    const { getRect, dispose: disposeRectCache } = createRectCache(el);

    // ---- per-frame flush (same shape as the wheel pump) ----
    let pendingZoomDelta = 0;
    let pendingPanDx = 0;
    let pendingPanDy = 0;
    let pendingPivot = { x: 0, y: 0 };
    let pendingRect: DOMRect | null = null;
    let raf = 0;
    const flush = () => {
      raf = 0;
      const rect = pendingRect;
      if (!rect) return;
      const sx = pendingPivot.x;
      const sy = pendingPivot.y;
      const zoomDelta = pendingZoomDelta;
      const panDx = pendingPanDx;
      const panDy = pendingPanDy;
      pendingZoomDelta = 0;
      pendingPanDx = 0;
      pendingPanDy = 0;
      pendingRect = null;
      if (zoomDelta === 0 && panDx === 0 && panDy === 0) return;
      const size = { width: rect.width, height: rect.height };
      const r = rootRef.current;
      let next = store.get();
      if (zoomDelta !== 0) {
        next = zoomAt(next, sx, sy, zoomDelta, size.width, size.height);
        next = maybeRebase(r, next, size, { x: sx, y: sy }, measure);
        next = clampZoom(r, next, sx, sy, size.width, size.height);
      }
      if (panDx !== 0 || panDy !== 0) {
        next = panVp(next, panDx, panDy);
        next = maybeRebase(r, next, size, undefined, measure);
      }
      // Space border — same rule as the wheel pipeline.
      next = clampPanRoam(r, next, size);
      store.set(next);
    };
    const begin = (sx: number, sy: number, rect: DOMRect) => {
      userNavigatedRef.current = true;
      onUserGesture?.();
      cancelAnim();
      stopInertia();
      pendingRect = rect;
      pendingPivot = { x: sx, y: sy };
      if (!raf) raf = requestAnimationFrame(flush);
    };

    // ---- inertia ----
    let inertiaRaf = 0;
    const stopInertia = () => {
      if (inertiaRaf) {
        cancelAnimationFrame(inertiaRaf);
        inertiaRaf = 0;
      }
    };
    const startInertia = (vx0: number, vy0: number) => {
      stopInertia();
      let vx = vx0;
      let vy = vy0;
      let prev = performance.now();
      const step = (now: number) => {
        inertiaRaf = 0;
        // Clamp dt to [0, 64]: the upper bound stops a background-tab stall
        // integrating into one huge jump; the lower bound guards the FIRST
        // frame against a rAF timestamp on a different epoch than
        // performance.now() (jsdom does this — a negative dt flips the
        // velocity into a giant backwards jump). Frame 2+ diffs rAF
        // timestamps against each other, so the epoch cancels out.
        const dt = Math.max(0, Math.min(now - prev, 64));
        prev = now;
        const decay = Math.exp(-dt / TOUCH.inertiaTau);
        vx *= decay;
        vy *= decay;
        if (Math.hypot(vx, vy) < TOUCH.inertiaStopSpeed) return;
        const rect = getRect();
        const size = { width: rect.width, height: rect.height };
        const r = rootRef.current;
        const next = maybeRebase(r, panVp(store.get(), vx * dt, vy * dt), size, undefined, measure);
        // Space border: the glide stops dead at the wall instead of
        // straining against the clamp until the velocity decays.
        const clamped = clampPanRoam(r, next, size);
        store.set(clamped);
        if (clamped !== next) return;
        inertiaRaf = requestAnimationFrame(step);
      };
      inertiaRaf = requestAnimationFrame(step);
    };

    // ---- gesture state ----
    // `t` = last event seen for the pointer. A finger whose node unmounted
    // mid-gesture (board rebase/cull) can lose its pointerup — the up
    // dispatches to the detached capture target and never bubbles here —
    // leaving a GHOST entry that turns the next single finger into a
    // "pinch". Two defenses: explicit container capture once a gesture
    // activates (the container never unmounts mid-gesture), and a
    // staleness purge on every pointerdown.
    const pts = new Map<number, { x: number; y: number; t: number }>();
    const STALE_MS = 10_000;
    const purgeStale = (now: number) => {
      for (const [id, p] of pts) {
        if (now - p.t > STALE_MS) pts.delete(id);
      }
    };
    const capture = (pointerId: number) => {
      try {
        el.setPointerCapture(pointerId);
      } catch {
        /* pointer already lifted */
      }
    };
    let mode: "idle" | "pan" | "pinch" = "idle";
    let downAt = 0;
    let downX = 0;
    let downY = 0;
    let downTarget: EventTarget | null = null;
    let lastX = 0;
    let lastY = 0;
    let pinch: { d: number; mx: number; my: number } | null = null;
    let samples: { t: number; x: number; y: number }[] = [];
    let lastTap: { t: number; x: number; y: number } | null = null;
    let lastDoubleTapAt = -Infinity;
    // True once a pan/pinch moved the board — the click the browser
    // synthesizes on release must not reach the pane (it would clear the
    // selection after every touch pan). Cleared by that click, or by the
    // next pointerdown when no click was synthesized.
    let gestureMoved = false;

    const handleDoubleTap = (x: number, y: number, target: EventTarget | null) => {
      lastDoubleTapAt = performance.now();
      const itemEl = (target as HTMLElement | null)?.closest?.(
        "[data-board-item]",
      ) as HTMLElement | null;
      const id = itemEl?.dataset.boardItem;
      if (id && onItemDoubleTap?.(id)) return;
      // Void (or unresolvable item): zoom in one step anchored at the tap.
      const rect = getRect();
      const sx = x - rect.left;
      const sy = y - rect.top;
      userNavigatedRef.current = true;
      onUserGesture?.();
      cancelAnim();
      stopInertia();
      const r = rootRef.current;
      const target2 = clampZoom(
        r,
        zoomAt(store.get(), sx, sy, TOUCH.doubleTapZoom, rect.width, rect.height),
        sx,
        sy,
        rect.width,
        rect.height,
      );
      animateTo(target2, () => {
        // Rebase once the step lands so a doubled-into space dives properly.
        store.set(
          maybeRebase(r, store.get(), { width: rect.width, height: rect.height }, { x: sx, y: sy }, measure),
        );
      });
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      stopInertia();
      const target = e.target as HTMLElement | null;
      // HUD & form controls own their touches (minimap drags itself) — but
      // board items do NOT: they expose role="button" for a11y, yet a finger
      // landing on a node must still pan the board, so the exclusion only
      // applies when the nearest interactive ancestor is not a board item.
      const interactive = target?.closest(
        "[data-board-item], [data-hud], button, [role='button'], a, input, textarea, select",
      );
      if (interactive && !interactive.hasAttribute("data-board-item")) return;
      const now = performance.now();
      purgeStale(now);
      if (pts.size >= 2) return; // ignore 3rd+ fingers
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY, t: now });
      if (pts.size === 1) {
        mode = "idle";
        gestureMoved = false;
        downAt = now;
        downX = lastX = e.clientX;
        downY = lastY = e.clientY;
        downTarget = e.target;
        samples = [{ t: downAt, x: e.clientX, y: e.clientY }];
      } else {
        // Second finger: any tap/pan in progress becomes a pinch. Capture
        // BOTH pointers to the container — implicit capture pins them to
        // their pointerdown targets, which the pinch itself can unmount
        // (auto dive-in re-renders the subtree) and a detached target's
        // events never reach us.
        mode = "pinch";
        gestureMoved = true;
        for (const id of pts.keys()) capture(id);
        const [a, b] = [...pts.values()];
        pinch = { d: Math.hypot(b.x - a.x, b.y - a.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
        cancelAnim();
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      const p = pts.get(e.pointerId);
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;
      p.t = performance.now();
      if (mode === "pinch") {
        if (pts.size < 2 || !pinch) return;
        const [a, b] = [...pts.values()];
        const d = Math.hypot(b.x - a.x, b.y - a.y);
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const rect = getRect();
        begin(mx - rect.left, my - rect.top, rect);
        if (pinch.d > 0 && d > 0) pendingZoomDelta += Math.log(d / pinch.d);
        pendingPanDx += mx - pinch.mx;
        pendingPanDy += my - pinch.my;
        pinch = { d, mx, my };
        return;
      }
      if (mode === "idle") {
        if (Math.hypot(e.clientX - downX, e.clientY - downY) < TOUCH.panSlop) return;
        mode = "pan";
        gestureMoved = true;
        // Same unmount hazard as pinch: panning can rebase the focal and
        // unmount the implicit capture target under the finger.
        capture(e.pointerId);
      }
      const rect = getRect();
      begin(e.clientX - rect.left, e.clientY - rect.top, rect);
      pendingPanDx += e.clientX - lastX;
      pendingPanDy += e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const now = performance.now();
      samples.push({ t: now, x: e.clientX, y: e.clientY });
      while (samples.length > 1 && now - samples[0].t > 120) samples.shift();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* not captured */
      }
      if (!pts.delete(e.pointerId)) return;
      if (mode === "pinch") {
        pinch = null;
        if (pts.size === 1) {
          // One finger lifted — the survivor keeps panning seamlessly. Its
          // stored position may be a frame stale; the next move diffs from
          // here, so re-anchor and reset the velocity window.
          mode = "pan";
          const [rem] = [...pts.values()];
          rem.t = performance.now();
          lastX = rem.x;
          lastY = rem.y;
          samples = [{ t: rem.t, x: rem.x, y: rem.y }];
        } else {
          mode = "idle";
        }
        return;
      }
      if (mode === "pan") {
        mode = "idle";
        if (e.type === "pointercancel") return;
        const now = performance.now();
        samples.push({ t: now, x: e.clientX, y: e.clientY });
        // Prune here too: a finger that dragged, PARKED, then lifted must
        // not inherit flick velocity from the pre-park samples.
        while (samples.length > 1 && now - samples[0].t > 120) samples.shift();
        const first = samples[0];
        const dt = Math.max(now - first.t, 1);
        // A real flick leaves a trail. One or two samples over a tiny
        // window (e.g. the wobble of lifting the second pinch finger, or a
        // stale re-anchored survivor) must not launch the board.
        if (samples.length < 3 || dt < 30) return;
        let vx = (e.clientX - first.x) / dt;
        let vy = (e.clientY - first.y) / dt;
        const speed = Math.hypot(vx, vy);
        if (speed > TOUCH.flickMaxSpeed) {
          vx *= TOUCH.flickMaxSpeed / speed;
          vy *= TOUCH.flickMaxSpeed / speed;
        }
        if (speed >= TOUCH.flickMinSpeed) startInertia(vx, vy);
        return;
      }
      // idle: tap / double-tap
      if (e.type === "pointercancel") return;
      const now = performance.now();
      if (now - downAt > TOUCH.tapMaxMs) return;
      if (
        lastTap &&
        now - lastTap.t <= TOUCH.doubleTapMs &&
        Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) <= TOUCH.doubleTapSlop
      ) {
        lastTap = null; // a triple tap shouldn't chain two opens
        handleDoubleTap(e.clientX, e.clientY, downTarget);
      } else {
        lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
    };

    // A focal item's long-press wants the pointer for a node drag. Allow it
    // only while the finger hasn't become a pan/pinch; on allow, the hook
    // forgets the pointer entirely (no tap, no pan) and the item takes over.
    const onNodeDragClaim = (e: Event) => {
      const pid = (e as CustomEvent<{ pointerId?: number }>).detail?.pointerId;
      if (typeof pid !== "number" || mode !== "idle" || !pts.has(pid)) {
        e.preventDefault();
        return;
      }
      pts.delete(pid);
      lastTap = null;
    };

    // ---- raw touch census (capture phase, sees HUD touches too) ----
    // Feeds the Safari gesture-event mute in useWheelZoom and the
    // contextmenu suppression below; deliberately independent of the
    // gesture map so a node-drag claim doesn't un-count a finger.
    const onRawDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") touchCountRef.current++;
    };
    const onRawUp = (e: PointerEvent) => {
      if (e.pointerType === "touch") touchCountRef.current = Math.max(0, touchCountRef.current - 1);
    };
    // Android fires contextmenu on long-press — it would pop the browser menu
    // mid-gesture and trigger the right-click unpin on pinned nodes.
    const onContextMenu = (e: Event) => {
      if (touchCountRef.current > 0) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onClickCapture = (e: MouseEvent) => {
      if (gestureMoved) {
        e.preventDefault();
        e.stopPropagation();
        gestureMoved = false;
      }
    };
    const onDblClickCapture = (e: MouseEvent) => {
      // We already handled the double-tap explicitly; eat the synthesized
      // dblclick (Chrome fires one, iOS Safari doesn't) so onDive can't
      // run twice.
      if (performance.now() - lastDoubleTapAt < 700) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // App switch / system gesture mid-touch: no pointerup ever arrives.
    // Drop all gesture state so the next touch starts clean.
    const onWindowBlur = () => {
      pts.clear();
      mode = "idle";
      pinch = null;
      lastTap = null;
      samples = [];
      stopInertia();
    };
    window.addEventListener("blur", onWindowBlur);

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener(NODE_DRAG_CLAIM_EVENT, onNodeDragClaim);
    el.addEventListener("pointerdown", onRawDown, { capture: true });
    el.addEventListener("pointerup", onRawUp, { capture: true });
    el.addEventListener("pointercancel", onRawUp, { capture: true });
    el.addEventListener("contextmenu", onContextMenu, { capture: true });
    el.addEventListener("click", onClickCapture, { capture: true });
    el.addEventListener("dblclick", onDblClickCapture, { capture: true });
    return () => {
      window.removeEventListener("blur", onWindowBlur);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener(NODE_DRAG_CLAIM_EVENT, onNodeDragClaim);
      el.removeEventListener("pointerdown", onRawDown, { capture: true });
      el.removeEventListener("pointerup", onRawUp, { capture: true });
      el.removeEventListener("pointercancel", onRawUp, { capture: true });
      el.removeEventListener("contextmenu", onContextMenu, { capture: true });
      el.removeEventListener("click", onClickCapture, { capture: true });
      el.removeEventListener("dblclick", onDblClickCapture, { capture: true });
      disposeRectCache();
      stopInertia();
      if (raf) cancelAnimationFrame(raf);
      touchCountRef.current = 0;
    };
  }, [enabled, containerRef, store, rootRef, userNavigatedRef, cancelAnim, onUserGesture, animateTo, onItemDoubleTap, touchCountRef]);
}

export interface KeyboardNavCommands {
  /** Escape — pop one focal level. */
  readonly popOut: () => void;
}

/** Keyboard navigation: esc pop, ⌘±/− zoom, arrow pan.
 *
 *  Listens on `window`, so when several boards share one store (the diff
 *  mirror) only ONE may enable this — otherwise each keypress is handled
 *  once per board and applied N× to the shared store (esc would pop
 *  multiple levels, ⌘+ would double-zoom). `enabled=false` makes the hook
 *  inert while preserving hook order. */
export function useKeyboardNav(
  deps: GestureDeps,
  commands: KeyboardNavCommands,
  enabled = true,
): void {
  const { store, containerRef, rootRef, userNavigatedRef, cancelAnim, onUserGesture } = deps;
  const { popOut } = commands;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") {
        e.preventDefault();
        popOut();
      } else if (
        (e.metaKey || e.ctrlKey) &&
        (e.key === "=" || e.key === "+" || e.key === "-" ||
         e.code === "Equal" || e.code === "Minus")
      ) {
        // ⌘+ / ⌘- zoom in/out — works everywhere including JCEF, where
        // trackpad pinch gestures are not reliably forwarded to the DOM.
        e.preventDefault();
        const zoomIn = e.key === "=" || e.key === "+" || e.code === "Equal";
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        userNavigatedRef.current = true;
        onUserGesture?.();
        cancelAnim();
        const size = { width: rect.width, height: rect.height };
        const r = rootRef.current;
        let next = zoomAt(store.get(), cx, cy, zoomIn ? ZOOM_GAIN.keyboard : -ZOOM_GAIN.keyboard, size.width, size.height);
        next = maybeRebase(r, next, size, undefined, measure);
        next = clampZoom(r, next, cx, cy, size.width, size.height);
        store.set(next);
      } else if (
        e.key === "ArrowUp" || e.key === "ArrowDown" ||
        e.key === "ArrowLeft" || e.key === "ArrowRight"
      ) {
        // Don't hijack arrows on focusable items — let Tab/arrow interactions
        // through if focus is on an item and a modifier isn't held.
        if (target?.closest("[data-board-item]") && !e.metaKey && !e.ctrlKey) return;
        e.preventDefault();
        const step = e.shiftKey ? 160 : 60;
        const dx = e.key === "ArrowLeft" ? step : e.key === "ArrowRight" ? -step : 0;
        const dy = e.key === "ArrowUp" ? step : e.key === "ArrowDown" ? -step : 0;
        userNavigatedRef.current = true;
        onUserGesture?.();
        cancelAnim();
        const kEl = containerRef.current;
        const kRect = kEl?.getBoundingClientRect();
        const kSize = kRect
          ? { width: kRect.width, height: kRect.height }
          : { width: 0, height: 0 };
        const moved = panVp(store.get(), dx, dy);
        store.set(clampPanRoam(rootRef.current, moved, kSize));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, popOut, cancelAnim, containerRef, store, rootRef, userNavigatedRef, onUserGesture]);
}
